import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, increment, getDoc, setDoc, collection, addDoc, runTransaction, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { db, functions } from '../firebase';
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { isSameDay } from "date-fns";
import { FaRegCalendarAlt, FaRegQuestionCircle } from "react-icons/fa";
import { format, addMonths } from "date-fns";
import { calculateShippingDistance } from '../services/distance';

const TAX_RATE = 0.13;
const INSURANCE_RATE = 0.02;
const PICKUP_FEE = 0;

function getNextFiveDays() {
    const days = [];
    const today = new Date();
    for (let i = 1; i <= 5; i++) { // 不包括今天，从明天开始
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        days.push(d.toISOString().slice(0, 10)); // yyyy-mm-dd
    }
    return days;
}

// 生成明天起连续5天的日期数组
function getNextFiveDaysDateObj() {
    const days = [];
    const today = new Date();
    for (let i = 1; i <= 5; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        days.push(d);
    }
    return days;
}

const Checkout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const CART_LOCAL_KEY = 'cart';
    const [grade, setGrade] = useState(0);
    const [insurance, setInsurance] = useState(false);
    const [deliverMethod, setDeliverMethod] = useState<'ship' | 'pickup'>('pickup');
    const [shipType, setShipType] = useState<'oneway' | 'twoway'>('oneway');
    const [houseStreet, setHouseStreet] = useState('');
    const [suite, setSuite] = useState('');
    const [city, setCity] = useState('');
    const [province, setProvince] = useState('Ontario');
    const [postalCode, setPostalCode] = useState('');
    const [products, setProducts] = useState<any[]>(location.state?.products || []);
    const [pickupDate, setPickupDate] = useState(getNextFiveDays()[0]);
    const [oneWayOption, setOneWayOption] = useState<'self_pickup' | 'self_return' | ''>('');
    const [showInsuranceTip, setShowInsuranceTip] = useState(false);
    const [rentalTerm, setRentalTerm] = useState<'1month' | '2month'>('1month');
    const [previewImg, setPreviewImg] = useState<string | null>(null);
    const [shippingDistance, setShippingDistance] = useState<number | null>(null);
    const [shippingFee, setShippingFee] = useState<number>(0);
    const [shippingFeeLoading, setShippingFeeLoading] = useState(false);
    const [colorMap, setColorMap] = useState<{ [colorId: string]: string }>({});

    useEffect(() => {
        const fetchGrade = async () => {
            if (user) {
                // 1. 先从 localStorage 读取
                const cached = localStorage.getItem(`user_${user.uid}`);
                const cacheTime = localStorage.getItem(`user_${user.uid}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 10 * 60 * 1000; // 10分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const userData = JSON.parse(cached);
                        setGrade(userData.grade || 0);
                        return;
                    } catch { }
                }

                // 2. 再请求 Firestore
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setGrade(userData.grade || 0);

                    // 3. 缓存用户数据
                    localStorage.setItem(`user_${user.uid}`, JSON.stringify(userData));
                    localStorage.setItem(`user_${user.uid}_time`, Date.now().toString());
                }
            }
        };
        fetchGrade();
    }, [user]);

    useEffect(() => {
        // 获取所有用到的 colorId
        const colorIds = Array.from(
            new Set(
                products
                    .flatMap(p => p.variants || [])
                    .map((v: any) => v.colorId)
            )
        );
        if (colorIds.length === 0) return;

        const fetchColors = async () => {
            const map: { [colorId: string]: string } = {};

            // 1. 先从 localStorage 读取已缓存的颜色
            const uncachedColorIds: string[] = [];
            for (const colorId of colorIds) {
                const cached = localStorage.getItem(`color_${colorId}`);
                const cacheTime = localStorage.getItem(`color_${colorId}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 10 * 60 * 1000; // 10分钟缓存

                if (cached && cacheAge < maxAge) {
                    try {
                        const colorData = JSON.parse(cached);
                        map[colorId] = colorData.name;
                    } catch {
                        uncachedColorIds.push(colorId);
                    }
                } else {
                    uncachedColorIds.push(colorId);
                }
            }

            // 2. 只请求未缓存的颜色
            if (uncachedColorIds.length > 0) {
                const snapshot = await getDocs(collection(db, 'colors'));
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (uncachedColorIds.includes(doc.id) && data.name) {
                        map[doc.id] = data.name;

                        // 3. 缓存颜色数据
                        localStorage.setItem(`color_${doc.id}`, JSON.stringify(data));
                        localStorage.setItem(`color_${doc.id}_time`, Date.now().toString());
                    }
                });
            }

            setColorMap(map);
        };

        fetchColors();
    }, [products]);

    const updateQuantity = (id: string, delta: number, variantId?: string) => {
        setProducts(prev => {
            const updatedProducts = prev.map(prod => {
                // 判断是否是当前商品和变体
                const isCurrent = prod.id === id && (variantId ? prod.variantId === variantId : !prod.variantId);
                if (isCurrent) {
                    // 判断库存
                    let available = 0;
                    if (prod.variants && prod.variantId) {
                        const variant = prod.variants.find((v: any) => v.colorId === prod.variantId);
                        available = variant ? variant.stock : 0;
                    } else {
                        available = typeof prod.availableNum === "number" ? prod.availableNum : 0;
                    }
                    const newQty = Math.max(1, prod.quantity + delta);
                    if (newQty > available) {
                        alert(`"${prod.title}" only has ${available} available in stock.`);
                        return prod;
                    }
                    return { ...prod, quantity: newQty };
                }
                return prod;
            });

            // 同步更新购物车
            updateCart(updatedProducts);
            return updatedProducts;
        });
    };
    const removeItem = (id: string, variantId?: string) => {
        setProducts(prev => {
            const newProds = prev.filter(prod =>
                prod.id !== id || (prod.variantId || '') !== (variantId || '')
            );
            updateCart(newProds); // 联动购物车
            return newProds;
        });
    };

    // 计算金额部分，修改subtotal逻辑
    const rentalMultiplier = rentalTerm === '2month' ? 1.7 : 1;
    const subtotal = products.reduce((sum, p) => sum + p.price * p.quantity, 0) * rentalMultiplier;
    const discount = 0;
    const insuranceFee = insurance ? subtotal * INSURANCE_RATE : 0;
    const tax = (subtotal - discount + insuranceFee + (deliverMethod === 'ship' ? shippingFee : PICKUP_FEE)) * TAX_RATE;
    const totalRaw = subtotal - discount + insuranceFee + (deliverMethod === 'ship' ? shippingFee : PICKUP_FEE) + tax;
    const total = Math.round(totalRaw * 100) / 100;

    // 下单时
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);

    const handlePlaceOrder = async () => {
        // 防止重复提交
        if (isPlacingOrder) {
            return;
        }

        setIsPlacingOrder(true);

        try {

            // 校验必填项
            if (products.length === 0) {
                alert("Please add items to place order.");
                setIsPlacingOrder(false);
                return;
            }
            if (deliverMethod === "ship") {
                if (!houseStreet.trim()) {
                    alert("Please enter your house and street.");
                    setIsPlacingOrder(false);
                    return;
                }
                if (!city.trim()) {
                    alert("Please enter your city.");
                    setIsPlacingOrder(false);
                    return;
                }
                if (!postalCode || postalCode.length !== 6) {
                    alert("Please enter a valid 6-digit postal code.");
                    setIsPlacingOrder(false);
                    return;
                }
                if (shippingFee === 0) {
                    alert("Please calculate shipping fee first.");
                    setIsPlacingOrder(false);
                    return;
                }
            }
            // 无论哪种方式都要校验手机号
            // if (!phone || phone.length !== 10) {
            //     alert("Please enter a valid 10-digit phone number.");
            //     return;
            // }

            // 检查商品库存
            for (const p of products) {
                // 1. 先从 localStorage 读取产品数据（短期缓存）
                const cached = localStorage.getItem(`product_${p.id}`);
                const cacheTime = localStorage.getItem(`product_${p.id}_time`);
                const now = Date.now();
                const cacheAge = cacheTime ? now - parseInt(cacheTime) : 0;
                const maxAge = 30 * 1000; // 30秒缓存（下单时需要相对实时）

                let data: any = null;

                if (cached && cacheAge < maxAge) {
                    try {
                        data = JSON.parse(cached);
                    } catch { }
                }

                // 2. 如果缓存不存在或过期，从Firestore获取
                if (!data) {
                    const prodRef = doc(db, "products", p.id);
                    const prodSnap = await getDoc(prodRef);
                    data = prodSnap.data();

                    if (data) {
                        // 3. 缓存产品数据（短期）
                        localStorage.setItem(`product_${p.id}`, JSON.stringify(data));
                        localStorage.setItem(`product_${p.id}_time`, Date.now().toString());
                    }
                }

                if (!data) {
                    alert(`Product "${p.title}" not found, please contact support.`);
                    setIsPlacingOrder(false);
                    return;
                }
                if (p.variantId) {
                    // 有 variant，检查对应 variant 的 stock
                    const variants = data.variants || [];
                    const variant = variants.find((v: any) => v.colorId === p.variantId);
                    if (!variant || typeof variant.stock !== "number") {
                        alert(`Product "${p.title}" has no stock info for this variant, please contact support.`);
                        setIsPlacingOrder(false);
                        return;
                    }
                    if (p.quantity > variant.stock) {
                        alert(`Product "${p.title}" (color) only has ${variant.stock} left, please adjust quantity.`);
                        setIsPlacingOrder(false);
                        return;
                    }
                } else {
                    // 没有 variant，检查 availableNum
                    if (typeof data.availableNum !== "number") {
                        alert(`Product "${p.title}" has no available stock info, please contact support.`);
                        setIsPlacingOrder(false);
                        return;
                    }
                    if (p.quantity > data.availableNum) {
                        alert(`Product "${p.title}" only has ${data.availableNum} left, please adjust quantity.`);
                        setIsPlacingOrder(false);
                        return;
                    }
                }
            }

            // 1. 新增订单
            if (!user) {
                alert("You must be logged in to place an order.");
                setIsPlacingOrder(false);
                return;
            }

            // 获取递增 orderId
            const orderId = await getNextOrderId();

            // 计算过期日期
            let expireDate = "";
            if (pickupDate) {
                const start = new Date(pickupDate);
                const months = rentalTerm === "2month" ? 2 : 1;
                expireDate = format(addMonths(start, months), "yyyy-MM-dd");
            }

            // 构造订单数据
            const orderData = {
                orderId, // 业务编号
                userId: user.uid,
                products: products.map(p => ({
                    productId: p.id,
                    title: p.title,
                    price: p.price,
                    quantity: p.quantity,
                    ...(p.variantId ? { variantId: p.variantId } : {})
                })),
                deliverMethod,
                shipType,
                oneWayOption,
                shippingFee,
                shippingDistance,
                houseStreet,
                suite,
                city,
                province,
                postalCode,
                pickupDate,
                rentalTerm,
                expireDate, // 新增字段
                insurance,
                subtotal: Number(subtotal.toFixed(2)),
                discount: Number(discount.toFixed(2)),
                insuranceFee: Number(insuranceFee.toFixed(2)),
                tax: Number(tax.toFixed(2)),
                total: Number(total.toFixed(2)),
                status: "new",
                createdAt: new Date(),
                return: false,
                pay: false,
                pickupstatus: false,
                viewed: false,
                deleted: false,
                amountPaid: 0,
                promotion: 0,
            };

            await addDoc(collection(db, "orders"), orderData);

            // 发送订单确认邮件
            try {
                // 获取用户信息
                const userDoc = await getDoc(doc(db, "users", user.uid));
                const userData = userDoc.data();
                const customerName = userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : 'Customer';
                const customerEmail = userData?.email || user.email;

                // 构造发货地址
                let deliveryAddress = '';
                if (deliverMethod === 'ship') {
                    deliveryAddress = `${houseStreet}${suite ? `, ${suite}` : ''}, ${city}, ${province} ${postalCode}`;
                }

                // 构造订单摘要
                const orderSummary = products.map(p => ({
                    title: p.title,
                    quantity: p.quantity,
                    price: p.price
                }));

                // 调用新的订单确认邮件Cloud Function
                const confirmOrderEmail = httpsCallable(functions, 'confirmOrderEmail');
                await confirmOrderEmail({
                    orderNumber: orderId,
                    customerName: customerName,
                    customerEmail: customerEmail,
                    orderDate: format(new Date(), 'MM/dd/yyyy'),
                    startDate: pickupDate ? format(new Date(pickupDate), 'MM/dd/yyyy') : '',
                    endDate: expireDate ? format(new Date(expireDate), 'MM/dd/yyyy') : '',
                    deliveryAddress: deliveryAddress,
                    deliverMethod: deliverMethod,
                    orderSummary: orderSummary
                });
            } catch (emailError) {
                // 邮件发送失败不影响订单创建，只记录错误
                console.error('Failed to send order confirmation email:', emailError);
            }

            // 2. 更新每个商品的库存
            for (const p of products) {
                const prodRef = doc(db, "products", p.id);

                if (p.variantId) {
                    // 有 variant，更新对应 variant 的 stock 和 reservedNum
                    await runTransaction(db, async (transaction) => {
                        const prodSnap = await transaction.get(prodRef);
                        if (!prodSnap.exists()) {
                            return;
                        }
                        const data = prodSnap.data();
                        const variants = data.variants || [];
                        const idx = variants.findIndex((v: any) => v.colorId === p.variantId);
                        if (idx !== -1) {
                            const oldStock = variants[idx].stock || 0;
                            const oldReserved = variants[idx].reservedNum || 0;

                            variants[idx].stock = oldStock - p.quantity;
                            variants[idx].reservedNum = oldReserved + p.quantity;

                            transaction.update(prodRef, { variants });
                        }
                    });
                } else {
                    // 没有 variant，直接更新产品 availableNum 和 reservedNum
                    try {
                        await updateDoc(prodRef, {
                            reservedNum: increment(p.quantity),
                            availableNum: increment(-p.quantity)
                        });
                    } catch (error) {
                        // 静默处理错误，不显示在控制台
                    }
                }
            }

            alert("Thank you for your order, our staff member will take care of the order & contact you!");

            // 清空购物车
            setProducts([]);
            if (user) {
                await setDoc(doc(db, "carts", user.uid), { items: [] });
                // 清空本地缓存
                localStorage.removeItem(`cart_${user.uid}`);
                localStorage.removeItem(`cart_${user.uid}_time`);
            } else {
                localStorage.removeItem(CART_LOCAL_KEY);
            }
            window.dispatchEvent(new Event('cart-updated'));

            // 重置订单状态
            setIsPlacingOrder(false);

            // 跳转到主页
            navigate("/");

        } catch (error) {
            alert("Order failed. Please try again or contact support.");
            setIsPlacingOrder(false);
        }
    };

    const updateCart = async (newCart: any[]) => {
        // newCart: [{ id, title, price, quantity, images, variantId? }]
        const cartItems = newCart.map(item => ({
            productId: item.id,
            quantity: item.quantity,
            ...(item.variantId ? { variantId: item.variantId } : {})
        }));
        if (user) {
            await setDoc(doc(db, "carts", user.uid), { items: cartItems });
            // 同步更新本地缓存
            localStorage.setItem(`cart_${user.uid}`, JSON.stringify({ items: cartItems }));
            localStorage.setItem(`cart_${user.uid}_time`, Date.now().toString());
        } else {
            localStorage.setItem(CART_LOCAL_KEY, JSON.stringify(cartItems));
        }
        window.dispatchEvent(new Event('cart-updated'));
    };

    const allowedDates = getNextFiveDaysDateObj();

    const isDateSelectable = (date: Date) =>
        allowedDates.some(d => isSameDay(d, date));

    // 计算shipping fee的函数
    const calculateShippingFee = async (postalCode: string) => {
        if (postalCode.length !== 6) return;

        setShippingFeeLoading(true);
        try {
            const result = await calculateShippingDistance(postalCode, shipType);
            setShippingDistance(result.distance);
            setShippingFee(result.fee);
            console.log(`Shipping fee calculated: CA$${result.fee} (Distance: ${result.distance}km)`);
        } catch (error: any) {
            console.error('Failed to calculate shipping fee:', error);
            setShippingDistance(null);
            setShippingFee(0);
            alert(`Failed to calculate shipping fee: ${error.message || 'Please enter manually'}`);
        } finally {
            setShippingFeeLoading(false);
        }
    };

    // 在 shipType 变化时自动重新计算 shipping fee
    useEffect(() => {
        if (
            deliverMethod === "ship" &&
            postalCode.length === 6
        ) {
            calculateShippingFee(postalCode);
        }
        // eslint-disable-next-line
    }, [shipType]);

    useEffect(() => {
        // 只在页面初始化时查一次库存
        const fetchAvailableNums = async () => {
            if (!products.length) return;
            // 获取所有 productId
            const ids = products.map(p => p.id);
            // 批量查找
            const q = query(collection(db, "products"), where("__name__", "in", ids));
            const snapshot = await getDocs(q);
            const availableMap: Record<string, number> = {};
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                availableMap[docSnap.id] = typeof data.availableNum === "number" ? data.availableNum : 0;
            });
            // 合并 availableNum 到 products
            setProducts(prev =>
                prev.map(p => ({
                    ...p,
                    availableNum: availableMap[p.id] ?? 0
                }))
            );
        };
        fetchAvailableNums();
        // eslint-disable-next-line
    }, []);

    return (
        <div style={{
            maxWidth: 900,
            margin: '0 auto',
            padding: 40,
            marginTop: 60,
            background: '#f9fafd',
            borderRadius: 18,
            boxShadow: '0 6px 32px rgba(0,0,0,0.08)'
        }}>
            <h1 style={{
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 32,
                letterSpacing: 1,
                color: '#222'
            }}>Checkout</h1>
            <div style={{
                display: 'flex',
                gap: 40,
                flexWrap: 'wrap'
            }}>
                {/* 商品列表 */}
                <div style={{ flex: 2, minWidth: 320 }}>
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            padding: 24,
                            marginBottom: 32,
                            maxHeight: 615,
                            overflowY: 'auto'
                        }}
                    >
                        <h2 style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#222' }}>Your Items</h2>
                        {products.length === 0 && (
                            <div style={{ color: '#888', textAlign: 'center', padding: 32 }}>No items in cart.</div>
                        )}
                        {products.map(prod => {
                            // 获取当前变体
                            const variant = prod.variants && prod.variantId
                                ? prod.variants.find((v: any) => v.colorId === prod.variantId)
                                : null;
                            const img = variant?.image || prod.images?.[0] || 'https://via.placeholder.com/60';
                            // 判断库存
                            const available = variant
                                ? (typeof variant.stock === 'number' ? variant.stock : 0)
                                : (typeof prod.availableNum === 'number' ? prod.availableNum : 0);

                            return (
                                <div key={prod.id + (prod.variantId || '')} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderBottom: '1px solid #f0f0f0',
                                    padding: '16px 0'
                                }}>
                                    <img
                                        src={img}
                                        alt={prod.title}
                                        style={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                            marginRight: 18,
                                            background: '#f5f5f5',
                                            cursor: img ? 'pointer' : 'default'
                                        }}
                                        onClick={() => img && setPreviewImg(img)}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 16 }}>{prod.title}</div>
                                        {variant && (
                                            <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>
                                                Color:
                                                {/* color 色号显示 */}
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 16, height: 16, borderRadius: '50%',
                                                    background: variant.colorCode, border: '1px solid #ccc', verticalAlign: 'middle', marginLeft: 4, marginRight: 4
                                                }} />
                                                {colorMap[variant.colorId] || ''}
                                            </div>
                                        )}
                                        <div style={{ color: '#888', fontSize: 14 }}>${prod.price}/month x {prod.quantity}</div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <button onClick={() => updateQuantity(prod.id, -1, prod.variantId)}
                                            disabled={prod.quantity <= 1}
                                            style={{
                                                border: 'none',
                                                background: '#f0f0f0',
                                                borderRadius: 4,
                                                width: 28,
                                                height: 28,
                                                fontWeight: 700,
                                                fontSize: 18,
                                                color: '#555',
                                                cursor: prod.quantity > 1 ? 'pointer' : 'not-allowed'
                                            }}>-</button>
                                        <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{prod.quantity}</span>
                                        <button onClick={() => updateQuantity(prod.id, 1, prod.variantId)}
                                            disabled={prod.quantity >= available}
                                            style={{
                                                border: 'none',
                                                background: '#f0f0f0',
                                                borderRadius: 4,
                                                width: 28,
                                                height: 28,
                                                fontWeight: 700,
                                                fontSize: 18,
                                                color: '#555',
                                                cursor: prod.quantity >= available ? 'not-allowed' : 'pointer'
                                            }}>+</button>
                                    </div>
                                    <button onClick={() => removeItem(prod.id, prod.variantId)}
                                        style={{
                                            marginLeft: 18,
                                            background: 'none',
                                            border: 'none',
                                            color: '#e74c3c',
                                            fontWeight: 600,
                                            fontSize: 15,
                                            cursor: 'pointer'
                                        }}>Remove</button>
                                    <div style={{
                                        width: 90,
                                        textAlign: 'right',
                                        fontWeight: 700,
                                        color: '#222'
                                    }}>${(prod.price * prod.quantity * (rentalTerm === '2month' ? 1.7 : 1)).toFixed(2)}</div>
                                </div>
                            );
                        })}
                    </div>
                    {/* 图片预览弹窗 */}
                    {previewImg && (
                        <div
                            onClick={() => setPreviewImg(null)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(0,0,0,0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9999,
                                cursor: 'zoom-out'
                            }}
                        >
                            <img
                                src={previewImg}
                                alt="Preview"
                                style={{
                                    maxWidth: '90vw',
                                    maxHeight: '80vh',
                                    borderRadius: 12,
                                    boxShadow: '0 4px 32px rgba(0,0,0,0.25)',
                                    background: '#fff'
                                }}
                                onClick={e => e.stopPropagation()}
                            />
                        </div>
                    )}
                </div>
                {/* 订单信息 */}
                <div style={{
                    flex: 1,
                    minWidth: 280,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    padding: 24,
                    height: 'fit-content'
                }}>
                    <h2 style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#222' }}>Order Summary</h2>
                    <div style={{ marginBottom: 10, color: '#444' }}>Subtotal: <span style={{ float: 'right' }}>${subtotal.toFixed(2)}</span></div>
                    {discount > 0 && (
                        <div style={{ marginBottom: 10, color: '#444' }}>
                            Discount ({grade}%): <span style={{ float: 'right', color: '#27ae60' }}>- ${discount.toFixed(2)}</span>
                        </div>
                    )}
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ color: '#444', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={insurance}
                                onChange={e => setInsurance(e.target.checked)}
                                style={{ marginRight: 6 }}
                            />
                            Insurance Fee ({(INSURANCE_RATE * 100).toFixed(0)}%)

                        </label>
                        <FaRegQuestionCircle
                            style={{ marginLeft: 6, cursor: 'pointer', verticalAlign: 'middle' }}
                            onClick={() => setShowInsuranceTip(v => !v)}
                            title="What is insurance fee?"
                        />
                        <span style={{ float: 'right', color: insurance ? '#222' : '#bbb' }}>
                            ${insuranceFee.toFixed(2)}
                        </span>
                        {/* 解释弹窗 */}
                        {showInsuranceTip && (
                            <div
                                style={{
                                    position: 'absolute',
                                    background: '#fff',
                                    color: '#222',
                                    border: '1px solid #eee',
                                    borderRadius: 8,
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                                    padding: '12px 18px',
                                    fontSize: 14,
                                    zIndex: 100,
                                    marginTop: 6,
                                    marginLeft: 10,
                                    maxWidth: 260
                                }}
                            >
                                Insurance fee explanation here...
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: 10, color: '#444' }}>
                        Deliver Method:
                        <select value={deliverMethod} onChange={e => setDeliverMethod(e.target.value as any)} style={{ marginLeft: 8, padding: 4, borderRadius: 4 }}>
                            <option value="ship">Ship</option>
                            <option value="pickup">Pick Up</option>
                        </select>
                    </div>
                    {deliverMethod === 'ship' && (
                        <>
                            <div style={{ marginBottom: 10, color: '#444' }}>
                                Ship Type:
                                <select
                                    value={shipType}
                                    onChange={e => {
                                        setShipType(e.target.value as any);
                                        if (e.target.value !== 'oneway') setOneWayOption('');
                                    }}
                                    style={{ marginLeft: 8, padding: 4, borderRadius: 4 }}
                                >
                                    <option value="oneway">One Way</option>
                                    <option value="twoway">Two Way</option>
                                </select>
                            </div>
                            {/* one way 时显示自提/自还选项 */}
                            {shipType === 'oneway' && (
                                <div style={{ marginBottom: 10, color: '#444' }}>
                                    One Way:
                                    <select
                                        value={oneWayOption}
                                        onChange={e => setOneWayOption(e.target.value as any)}
                                        style={{ marginLeft: 8, padding: 4, borderRadius: 4 }}
                                    >
                                        <option value="">Select Option</option>
                                        <option value="self_pickup">I will pick up by myself</option>
                                        <option value="self_return">I will return by myself</option>
                                    </select>
                                </div>
                            )}
                            {/* Shipping Address 分字段输入 */}
                            <div style={{ marginBottom: 10 }}>
                                <input
                                    type="text"
                                    placeholder="House #, Street Name *"
                                    value={houseStreet}
                                    onChange={e => setHouseStreet(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        borderRadius: 6,
                                        border: '1px solid #ddd',
                                        marginTop: 4
                                    }}
                                    required={deliverMethod === 'ship'}
                                />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <input
                                    type="text"
                                    placeholder="Suite Number (optional)"
                                    value={suite}
                                    onChange={e => setSuite(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        borderRadius: 6,
                                        border: '1px solid #ddd',
                                        marginTop: 4
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <input
                                    type="text"
                                    placeholder="City *"
                                    value={city}
                                    onChange={e => setCity(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        borderRadius: 6,
                                        border: '1px solid #ddd',
                                        marginTop: 4
                                    }}
                                    required={deliverMethod === 'ship'}
                                />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <select
                                    value={province}
                                    onChange={e => setProvince(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        borderRadius: 6,
                                        border: '1px solid #ddd',
                                        marginTop: 4,
                                        background: '#fff'
                                    }}
                                    disabled
                                >
                                    <option value="Ontario">Ontario</option>
                                </select>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <input
                                    type="text"
                                    placeholder="Postal Code *"
                                    value={postalCode}
                                    onChange={e => {
                                        setPostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6));
                                        setShippingDistance(null);
                                        setShippingFee(0);
                                    }}
                                    onBlur={() => {
                                        if (postalCode.length === 6) {
                                            calculateShippingFee(postalCode);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        borderRadius: 6,
                                        border: '1px solid #ddd',
                                        marginTop: 4
                                    }}
                                    required={deliverMethod === 'ship'}
                                    maxLength={6}
                                />
                                {shippingFeeLoading && (
                                    <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                                        Calculating shipping fee...
                                    </div>
                                )}
                                {!shippingFeeLoading && shippingDistance !== null && (
                                    <div style={{ color: '#27ae60', fontSize: 13, marginTop: 4 }}>
                                        Distance: {shippingDistance} KM - Shipping Fee: ${shippingFee}
                                    </div>
                                )}
                                {!shippingFeeLoading && postalCode.length === 6 && shippingDistance === null && shippingFee === 0 && (
                                    <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 4 }}>
                                        Unable to calculate shipping fee. Please contact us.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {/* 手机号输入框始终显示在这里 */}
                    {/* 
                    <div style={{ marginBottom: 10 }}>
                        <input
                            type="tel"
                            placeholder="Enter phone number *"
                            value={formatPhoneNumber(phone)}
                            onChange={e => {
                                setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                            }}
                            style={{
                                width: '100%',
                                padding: 8,
                                borderRadius: 6,
                                border: '1px solid #ddd',
                                marginTop: 4
                            }}
                            required
                            pattern="\d{3}-\d{3}-\d{4}"
                            maxLength={12}
                        />

                    </div>
                    */}
                    {/* Delivery Date / Pick Up Date */}
                    <div style={{ marginBottom: 10, color: '#444' }}>
                        {deliverMethod === 'ship' ? 'Delivery Date:' : 'Pick Up Date:'}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: 6
                        }}>
                            <ReactDatePicker
                                selected={pickupDate ? new Date(pickupDate) : null}
                                onChange={date => setPickupDate(date ? format(date, "yyyy-MM-dd") : "")}
                                filterDate={isDateSelectable}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Select a date"
                                minDate={allowedDates[0]}
                                maxDate={allowedDates[allowedDates.length - 1]}
                                calendarStartDay={1}
                                showPopperArrow={false}
                                wrapperClassName="checkout-datepicker"
                                customInput={
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid #ddd',
                                            borderRadius: 6,
                                            padding: '8px 14px',
                                            background: '#fff',
                                            cursor: 'pointer',
                                            minWidth: 160,
                                            fontWeight: 600,
                                            fontSize: 16,
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                                        }}
                                    >
                                        <span style={{ flex: 1, textAlign: 'center' }}>
                                            {pickupDate ? pickupDate : "Select a date"}
                                        </span>
                                        <FaRegCalendarAlt style={{ marginLeft: 10, color: 'lightblue', fontSize: 20 }} />
                                    </div>
                                }
                            />
                        </div>
                    </div>

                    {/* Select rental term */}
                    <div style={{ marginBottom: 16, color: '#444', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 10 }}>Select Rental Term:</span>
                        <select
                            value={rentalTerm}
                            onChange={e => setRentalTerm(e.target.value as '1month' | '2month')}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 6,
                                border: '1px solid #ddd',
                                fontWeight: 600,
                                fontSize: 16,
                                background: '#fff'
                            }}
                        >
                            <option value="1month">1 Month</option>
                            <option value="2month">2 Months</option>
                        </select>
                    </div>

                    <div style={{ marginBottom: 10, color: '#444' }}>Shipping Fee: <span style={{ float: 'right' }}>${(deliverMethod === 'ship' ? shippingFee : PICKUP_FEE).toFixed(2)}</span></div>
                    <div style={{ marginBottom: 10, color: '#444' }}>Tax ({(TAX_RATE * 100).toFixed(0)}%): <span style={{ float: 'right' }}>${tax.toFixed(2)}</span></div>
                    <div style={{
                        fontWeight: 800,
                        fontSize: 22,
                        margin: '18px 0 10px 0',
                        color: '#222'
                    }}>
                        Order Amount: <span style={{ float: 'right', color: '#27ae60' }}>${total.toFixed(2)}</span>
                    </div>
                    <div style={{ marginTop: 18, marginBottom: 18 }}>
                        <div style={{ color: '#444', fontWeight: 500, marginBottom: 8 }}>Payment Method:</div>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            background: '#f5f5f5',
                            borderRadius: 6,
                            padding: '10px 12px',
                            cursor: 'not-allowed',
                            opacity: 0.7
                        }}>
                            <input
                                type="radio"
                                name="payment"
                                checked
                                disabled
                                style={{ accentColor: '#27ae60' }}
                            />
                            Offline Pay (we'll contact you for the payment)
                        </label>
                    </div>
                    <button
                        style={{
                            width: '100%',
                            background: isPlacingOrder ? '#95a5a6' : '#27ae60',
                            color: '#fff',
                            padding: '14px 0',
                            border: 'none',
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: 20,
                            cursor: isPlacingOrder ? 'not-allowed' : 'pointer',
                            marginTop: 10,
                            boxShadow: '0 2px 8px rgba(39,174,96,0.08)',
                            opacity: isPlacingOrder ? 0.7 : 1
                        }}
                        onClick={handlePlaceOrder}
                        disabled={isPlacingOrder}
                    >
                        {isPlacingOrder ? 'Processing Order...' : 'Place Order'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Checkout;

// 下单时获取递增 orderId
const getNextOrderId = async () => {
    const counterRef = doc(db, "counters", "orders");
    const newOrderId = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        let current = 0;
        if (counterDoc.exists()) {
            current = counterDoc.data().current || 0;
        }
        const next = current + 1;
        transaction.set(counterRef, { current: next }, { merge: true });
        return next;
    });
    // 格式化为6位字符串
    return String(newOrderId).padStart(6, "0");
};